import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { FaInstagram, FaTiktok, FaFacebook, FaWhatsapp } from 'react-icons/fa';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'À Propos', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Football Academy', path: '/football' },
    { name: 'Installations', path: '/facilities' },
    { name: 'Contact', path: '/contact' }
  ];

  const services = [
    'Coaching Privé',
    'Coaching Duo',
    'Coaching Groupe',
    'Coaching En Ligne',
    'Programme Nutrition'
  ];

  return (
    <footer className="bg-zinc-950 border-t border-zinc-800">
      {/* Main Footer */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_em-fitness/artifacts/l1grm42g_IMG_7129.jpeg"
              alt="EM Coaching"
              className="h-20 mb-6"
            />
            <p className="text-gray-400 mb-6 leading-relaxed">
              Plus de 17 ans d'expérience dans le sport et 6 ans de coaching professionnel 
              au service de votre transformation.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/coach.massa"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-purple-600 via-pink-600 to-orange-500 flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg hover:shadow-pink-500/50">
                  <FaInstagram className="w-6 h-6 text-white" />
                </div>
              </a>
              <a
                href="https://www.tiktok.com/@coach.massa"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="w-12 h-12 rounded-full bg-black border-2 border-cyan-400 flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg hover:shadow-cyan-400/50">
                  <FaTiktok className="w-6 h-6 text-white" />
                </div>
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="w-12 h-12 rounded-full bg-[#1877F2] flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg hover:shadow-blue-500/50">
                  <FaFacebook className="w-6 h-6 text-white" />
                </div>
              </a>
              <a
                href="https://wa.me/32489822709?text=Bonjour,%20je%20souhaite%20des%20informations%20sur%20les%20coachings%20EM%20Coaching"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
                title="Contactez-nous sur WhatsApp"
              >
                <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg hover:shadow-green-500/50">
                  <FaWhatsapp className="w-6 h-6 text-white" />
                </div>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Navigation</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-red-600 transition-colors flex items-center group"
                  >
                    <span className="w-0 h-0.5 bg-red-600 group-hover:w-4 transition-all duration-300 mr-2"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Nos Services</h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to="/services"
                    className="text-gray-400 hover:text-red-600 transition-colors flex items-center group"
                  >
                    <span className="w-0 h-0.5 bg-red-600 group-hover:w-4 transition-all duration-300 mr-2"></span>
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start group">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <a
                    href="tel:0489822709"
                    className="text-gray-300 hover:text-red-600 transition-colors font-medium"
                  >
                    0489 82 27 09
                  </a>
                  <p className="text-gray-500 text-sm">WhatsApp disponible</p>
                </div>
              </li>
              <li className="flex items-start group">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <a
                    href="mailto:massa_manu@outlook.com"
                    className="text-gray-300 hover:text-red-600 transition-colors break-all text-sm"
                  >
                    massa_manu@outlook.com
                  </a>
                </div>
              </li>
              <li className="flex items-start group">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 group-hover:scale-110 transition-transform">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm">
                    Av. de la Basilique 14<br />
                    1082 Berchem-Sainte-Agathe
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-zinc-800 bg-black">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">
              © {currentYear} EM COACHING. Tous droits réservés.
            </p>
            <p className="text-gray-500 text-sm">
              Conçu avec passion par <span className="text-red-600">Coach Massa</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
